/**
 * InlineGantt  –  custom combined data-grid + timeline, no third-party
 * library, no watermarks, full MUI theme support (light + dark).
 *
 * Layout:
 *   ┌────────── LEFT GRID ───────────┬──────── CHART ────────────────┐
 *   │  col headers                   │  month header                 │
 *   │  row 0 data cells              │  bar 0                        │
 *   │  row 1 data cells              │  bar 1                        │
 *   │  …                             │  …                            │
 *   └────────────────────────────────┴───────────────────────────────┘
 *   Both sides share one virtual scroll so rows stay aligned.
 */
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Tooltip,
  Typography,
  useTheme,
  Switch,
  IconButton,
  Chip,
} from "@mui/material";
import ZoomInIcon  from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import TodayIcon   from "@mui/icons-material/Today";

import {
  TYPE_META,
  TYPE_BY_FOUR_D,
  enrichRow,
  parseDate,
  fmtDate,
  dateToX,
  computeRange,
  buildMonths,
} from "./ganttUtils";
import { normalizeSi } from "../../../MCS3/Schedule4D/siUtils";

/* ── Constants ─────────────────────────────────────────────────────────── */
const ROW_H        = 44;          // px per data row
const HEADER_H     = 48;          // px for column / month header
const MONTH_BASE_W = 72;          // px per month column at zoom 1
const MIN_BAR_W    = 5;
const SPLITTER_W   = 5;

/* ── Column definition ──────────────────────────────────────────────────── */
const COLUMNS = [
  { key: "icon", label: "", w: 28, align: "center" },
  { key: "task", label: "Task / Group", w: 200, align: "left" },
  { key: "uid", label: "UID #", w: 88, align: "left" },
  { key: "fourD", label: "4D", w: 48, align: "center" },
  { key: "wbsId", label: "WBS", w: 72, align: "left" },
  { key: "si", label: "SI", w: 48, align: "center" },
  { key: "plannedStart", label: "Planned Start", w: 100, align: "center" },
  { key: "plannedEnd", label: "Planned Finish", w: 100, align: "center" },
  { key: "revisedStart", label: "Rev. Start", w: 100, align: "center" },
  { key: "revisedEnd", label: "Rev. Finish", w: 100, align: "center" },
  { key: "actualStart", label: "Actual Start", w: 100, align: "center" },
  { key: "actualEnd", label: "Actual Finish", w: 100, align: "center" },
  { key: "budgetedHrDollar", label: "Budget Hrs/$", w: 82, align: "right" },
  { key: "actualHrDollar", label: "Actual Hrs/$", w: 82, align: "right" },
  { key: "diff", label: "Diff %", w: 66, align: "right" },
];

const GRID_W = COLUMNS.reduce((s, c) => s + c.w, 0);

/* ── Tiny helpers ────────────────────────────────────────────────────────── */
const fmtHrs = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n.toFixed(2) : "—";
};
const diffPct = (b, a) => {
  const bv = Number(b) || 0;
  const av = Number(a) || 0;
  if (bv === 0 && av === 0) return null;
  return bv !== 0 ? ((bv - av) / bv) * 100 : null;
};

/* ═══════════════════════════════════════════════════════════════════════════
   CELL RENDERER
═══════════════════════════════════════════════════════════════════════════ */
const Cell = ({ col, row, onSiChange, siUpdatingIds, theme }) => {
  const v = row[col.key];

  if (col.key === "icon") {
    const type = (row.type || "").toLowerCase();
    const meta = TYPE_META[type] || {};
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
          userSelect: "none",
        }}
        title={meta.label || type}
      >
        {meta.icon || "📋"}
      </Box>
    );
  }

  if (col.key === "fourD") {
    const meta = TYPE_BY_FOUR_D[v] || {};
    return v ? (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: "5px",
          py: "1px",
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: 700,
          color: "#fff",
          bgcolor: meta.color || "grey.600",
          letterSpacing: "0.4px",
          whiteSpace: "nowrap",
        }}
      >
        {v}
      </Box>
    ) : (
      <span style={{ color: theme.palette.text.disabled }}>—</span>
    );
  }

  if (col.key === "si") {
    const status  = row._siStatus;
    const color   = row._barColor;
    const taskId  = row.scheduleTaskId ?? row.id;
    const busy    = siUpdatingIds instanceof Set && siUpdatingIds.has(String(taskId));
    const checked = normalizeSi(row.si) === 1;
    const label   = status === "complete" ? "Complete" : status === "overdue" ? "Overdue" : "In Progress";
    return (
      <Tooltip title={label} placement="top">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          {onSiChange ? (
            <Switch
              size="small"
              checked={checked}
              disabled={busy}
              onChange={() => onSiChange(row)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                "& .MuiSwitch-thumb": { bgcolor: color },
                "& .MuiSwitch-track": { bgcolor: `${color}88` },
              }}
            />
          ) : (
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: color,
                boxShadow: `0 0 0 2px ${color}44`,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  if (col.key === "task") {
    return (
      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: col.w - 8 }}>
        {v || "—"}
      </Box>
    );
  }

  if (["plannedStart","plannedEnd","revisedStart","revisedEnd","actualStart","actualEnd"].includes(col.key)) {
    return <span>{fmtDate(v)}</span>;
  }

  if (col.key === "budgetedHrDollar" || col.key === "actualHrDollar") {
    return <span>{fmtHrs(v)}</span>;
  }

  if (col.key === "diff") {
    const pct = diffPct(row.budgetedHrDollar, row.actualHrDollar);
    if (pct === null) return <span style={{ color: theme.palette.text.disabled }}>—</span>;
    const color = pct < 0 ? "#ef5350" : pct > 0 ? "#66bb6a" : "inherit";
    return <span style={{ color, fontWeight: 600 }}>{(pct > 0 ? "+" : "") + pct.toFixed(1)}%</span>;
  }

  return <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v ?? "—"}</span>;
};

/* ═══════════════════════════════════════════════════════════════════════════
   TOOLTIP CARD
═══════════════════════════════════════════════════════════════════════════ */
const TooltipCard = ({ row }) => {
  const type = (row.type || "").toLowerCase();
  const meta = TYPE_META[type] || {};
  return (
    <Box sx={{ p: 1, minWidth: 200, fontSize: "12px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        <span style={{ fontSize: 16 }}>{meta.icon || "📋"}</span>
        <Typography variant="body2" fontWeight={700} noWrap>{row.task || row.name || "—"}</Typography>
        {meta.label && (
          <Chip label={meta.label} size="small" sx={{ bgcolor: meta.color, color: "#fff", fontSize: 10, height: 18 }} />
        )}
      </Box>
      {[
        ["WBS",        row.wbsId],
        ["UID",        row.uid],
        ["SI Status",  row._siStatus],
        ["Planned",    `${fmtDate(row.plannedStart)} → ${fmtDate(row.plannedEnd)}`],
        row.revisedStart ? ["Revised",   `${fmtDate(row.revisedStart)} → ${fmtDate(row.revisedEnd)}`]   : null,
        row.actualStart  ? ["Actual",    `${fmtDate(row.actualStart)} → ${fmtDate(row.actualEnd)}`]    : null,
        row.budgetedHrDollar > 0 ? ["Budget Hrs", fmtHrs(row.budgetedHrDollar)] : null,
        row.actualHrDollar   > 0 ? ["Actual Hrs", fmtHrs(row.actualHrDollar)]   : null,
        row.mrqNumber ? ["MRQ #", row.mrqNumber] : null,
      ].filter(Boolean).map(([k, val]) => (
        <Box key={k} sx={{ display: "flex", gap: 1, lineHeight: 1.6 }}>
          <Box component="span" sx={{ color: "text.secondary", minWidth: 80 }}>{k}:</Box>
          <Box component="span" sx={{ color: "text.primary" }}>{val || "—"}</Box>
        </Box>
      ))}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const InlineGantt = ({
  rows = [],
  onSiChange,
  siUpdatingIds,
  onRowClick,
}) => {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";
  const scrollEl = useRef(null);
  const [zoom, setZoom]           = useState(1);          // month-width multiplier
  const [gridW, setGridW]         = useState(GRID_W);     // resizable splitter
  const [dragging, setDragging]   = useState(false);
  const dragStartX                = useRef(0);
  const dragStartW                = useRef(0);

  /* Enrich rows */
  const enriched = useMemo(() => rows.map(enrichRow), [rows]);

  /* Date range */
  const range   = useMemo(() => computeRange(enriched), [enriched]);
  const months  = useMemo(() => buildMonths(range.start, range.end), [range]);
  const totalMs = range.end.getTime() - range.start.getTime();
  const monthW  = MONTH_BASE_W * zoom;
  const chartW  = months.length * monthW;

  /* Today X */
  const todayX = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today < range.start || today > range.end) return null;
    return dateToX(today, range.start, totalMs, chartW);
  }, [range, totalMs, chartW]);

  /* Scroll to today on mount */
  useEffect(() => {
    if (todayX !== null && scrollEl.current) {
      scrollEl.current.scrollLeft = Math.max(0, todayX - 200);
    }
  }, [todayX]);

  /* Splitter drag */
  const onSplitterDown = (e) => {
    setDragging(true);
    dragStartX.current = e.clientX;
    dragStartW.current = gridW;
    e.preventDefault();
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const delta = e.clientX - dragStartX.current;
      setGridW(Math.max(200, Math.min(900, dragStartW.current + delta)));
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging]);

  /* Zoom handlers */
  const zoomIn  = useCallback(() => setZoom((z) => Math.min(z * 1.4, 8)),  []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z / 1.4, 0.3)), []);
  const scrollToToday = useCallback(() => {
    if (todayX !== null && scrollEl.current)
      scrollEl.current.scrollLeft = Math.max(0, todayX - 200);
  }, [todayX]);

  /* Colours */
  const borderColor    = theme.palette.divider;
  const headerBg       = isDark ? theme.palette.background.default : "#f5f5f5";
  const rowEvenBg      = isDark ? theme.palette.action.hover        : "#fafafa";
  const rowHoverBg     = isDark ? theme.palette.action.selected     : "#f0f4ff";
  const textPrimary    = theme.palette.text.primary;
  const textSecondary  = theme.palette.text.secondary;
  const paperBg        = theme.palette.background.paper;

  /* Shared cell style */
  const cellSx = (col, rowIdx) => ({
    minWidth: col.w,
    maxWidth: col.w,
    width:    col.w,
    height:   ROW_H,
    display:  "flex",
    alignItems: "center",
    justifyContent: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
    px: "6px",
    fontSize: "12px",
    color: textPrimary,
    borderRight: "1px solid",
    borderColor,
    borderBottom: "1px solid",
    overflow: "hidden",
    bgcolor: rowIdx % 2 === 1 ? rowEvenBg : "transparent",
    boxSizing: "border-box",
    flexShrink: 0,
    "&:hover": { bgcolor: rowHoverBg },
  });

  /* ── Bar for a single row ─────────────────────────────────────────── */
  const renderBar = (row, rowIdx) => {
    const start = parseDate(row.plannedStart);
    const end   = parseDate(row.plannedEnd);
    const type  = (row.type || "").toLowerCase();
    const meta  = TYPE_META[type] || {};
    const bgRow = rowIdx % 2 === 1 ? rowEvenBg : paperBg;

    return (
      <Tooltip
        key={row.id ?? rowIdx}
        title={<TooltipCard row={row} />}
        placement="top-start"
        arrow
      >
        <Box
          onClick={onRowClick ? () => onRowClick({ original: row }) : undefined}
          sx={{
            position: "relative",
            height: ROW_H,
            minHeight: ROW_H,
            maxHeight: ROW_H,
            width: chartW,
            borderBottom: "1px solid",
            borderColor,
            bgcolor: bgRow,
            cursor: onRowClick ? "pointer" : "default",
            flexShrink: 0,
            "&:hover": { bgcolor: rowHoverBg },
            boxSizing: "border-box",
          }}
        >
          {start && end && (
            (() => {
              const x1 = dateToX(start, range.start, totalMs, chartW);
              const x2 = dateToX(new Date(end.getTime() + 86_400_000), range.start, totalMs, chartW);
              const w  = Math.max(MIN_BAR_W, x2 - x1);
              return (
                <Box
                  sx={{
                    position: "absolute",
                    left:     x1,
                    width:    w,
                    top:      "50%",
                    transform:"translateY(-50%)",
                    height:   ROW_H * 0.52,
                    bgcolor:  row._barColor,
                    borderRadius: "4px",
                    boxShadow: 1,
                    display:  "flex",
                    alignItems: "center",
                    px:       "5px",
                    overflow: "hidden",
                    gap:      "3px",
                    whiteSpace: "nowrap",
                    fontSize: "10px",
                    color:    "#fff",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ flexShrink: 0, fontSize: "11px" }}>{meta.icon || "📋"}</span>
                  {w > 40 && (
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(row.task || row.name || "").trim()}
                    </span>
                  )}
                </Box>
              );
            })()
          )}
          {/* Revised date thin overlay bar */}
          {row.revisedStart && row.revisedEnd && (() => {
            const rs = parseDate(row.revisedStart);
            const re = parseDate(row.revisedEnd);
            if (!rs || !re) return null;
            const x1 = dateToX(rs, range.start, totalMs, chartW);
            const x2 = dateToX(new Date(re.getTime() + 86_400_000), range.start, totalMs, chartW);
            const w  = Math.max(MIN_BAR_W, x2 - x1);
            return (
              <Box
                sx={{
                  position:  "absolute",
                  left:       x1,
                  width:      w,
                  top:        "50%",
                  transform:  "translateY(-50%) translateY(10px)",
                  height:     3,
                  bgcolor:    "#90caf9",
                  borderRadius: "2px",
                  opacity:    0.85,
                }}
                title="Revised dates"
              />
            );
          })()}
        </Box>
      </Tooltip>
    );
  };

  /* ── Month grid lines (vertical) ─────────────────────────────────── */
  const gridLines = months.map((m, i) => (
    <Box
      key={i}
      sx={{
        position: "absolute",
        top: 0, bottom: 0,
        left: i * monthW,
        width: "1px",
        bgcolor: borderColor,
        opacity: 0.5,
        pointerEvents: "none",
      }}
    />
  ));

  return (
    <Box
      sx={{
        bgcolor: paperBg,
        border:  "1px solid",
        borderColor,
        borderRadius: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "min(78vh, 860px)",
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display:    "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor,
          bgcolor:    headerBg,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
          Planned Timeline
        </Typography>
        <Tooltip title="Zoom in"><IconButton size="small" onClick={zoomIn}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Zoom out"><IconButton size="small" onClick={zoomOut}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Scroll to today"><IconButton size="small" onClick={scrollToToday}><TodayIcon fontSize="small" /></IconButton></Tooltip>

        <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
          {[
            { label: "Week",    z: 2.2 },
            { label: "Month",   z: 1   },
            { label: "Quarter", z: 0.4 },
          ].map(({ label, z }) => (
            <Box
              key={label}
              component="button"
              onClick={() => setZoom(z)}
              sx={{
                px: 1.5, py: "2px", border: "1px solid", borderColor,
                borderRadius: "4px", cursor: "pointer", fontSize: "11px",
                bgcolor: Math.abs(zoom - z) < 0.05 ? "primary.main" : "transparent",
                color:   Math.abs(zoom - z) < 0.05 ? "#fff" : textSecondary,
                fontWeight: 600, "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
          {[
            { color: "#4caf50", label: "Complete" },
            { color: "#ffc107", label: "Pending"  },
            { color: "#f44336", label: "Overdue"  },
          ].map(({ color, label }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: textSecondary }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              {label}
            </Box>
          ))}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: textSecondary }}>
            <Box sx={{ width: 18, height: 3, bgcolor: "#90caf9", borderRadius: 1, flexShrink: 0 }} />
            Revised
          </Box>
        </Box>
      </Box>

      {/* ── Main body: grid + splitter + chart ───────────────────────── */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* LEFT GRID */}
        <Box sx={{ width: gridW, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Column header */}
          <Box
            sx={{
              display:    "flex",
              height:      HEADER_H,
              bgcolor:     headerBg,
              borderBottom:"1px solid",
              borderColor,
              flexShrink:  0,
              position:   "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            {COLUMNS.map((col) => (
              <Box
                key={col.key}
                sx={{
                  minWidth: col.w, maxWidth: col.w, width: col.w,
                  display: "flex", alignItems: "center",
                  justifyContent: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
                  px: "6px",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: textSecondary,
                  borderRight: "1px solid",
                  borderColor,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>

          {/* Rows */}
          <Box sx={{ overflowY: "auto", overflowX: "auto", flex: 1 }}>
            {enriched.map((row, rowIdx) => (
              <Box
                key={row.id ?? rowIdx}
                onClick={onRowClick ? () => onRowClick({ original: row }) : undefined}
                sx={{
                  display: "flex",
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover > *": { bgcolor: `${rowHoverBg} !important` },
                }}
              >
                {COLUMNS.map((col) => (
                  <Box key={col.key} sx={cellSx(col, rowIdx)}>
                    <Cell
                      col={col}
                      row={row}
                      onSiChange={onSiChange}
                      siUpdatingIds={siUpdatingIds}
                      theme={theme}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* SPLITTER */}
        <Box
          onMouseDown={onSplitterDown}
          sx={{
            width:     SPLITTER_W,
            flexShrink:0,
            bgcolor:   borderColor,
            cursor:    "col-resize",
            "&:hover": { bgcolor: "primary.main", opacity: 0.7 },
            transition: "background-color 0.15s",
          }}
        />

        {/* RIGHT CHART */}
        <Box
          ref={scrollEl}
          sx={{
            flex:       1,
            overflowX: "auto",
            overflowY: "auto",
            display:   "flex",
            flexDirection: "column",
          }}
        >
          {/* Month header */}
          <Box
            sx={{
              display:    "flex",
              height:      HEADER_H,
              minWidth:    chartW,
              bgcolor:     headerBg,
              borderBottom:"1px solid",
              borderColor,
              position:   "sticky",
              top: 0,
              zIndex: 2,
              flexShrink: 0,
            }}
          >
            {months.map((m, i) => (
              <Box
                key={i}
                sx={{
                  minWidth: monthW,
                  maxWidth: monthW,
                  height:   "100%",
                  display:  "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: "1px solid",
                  borderColor,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: textSecondary,
                  userSelect: "none",
                }}
              >
                {m.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              </Box>
            ))}
          </Box>

          {/* Chart rows */}
          <Box sx={{ position: "relative", minWidth: chartW, flex: 1 }}>
            {/* Vertical grid lines */}
            {gridLines}

            {/* Today marker */}
            {todayX !== null && (
              <Box
                sx={{
                  position:  "absolute",
                  top: 0, bottom: 0,
                  left:       todayX,
                  width:      "2px",
                  bgcolor:   "primary.main",
                  opacity:    0.8,
                  zIndex:     1,
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    position:   "absolute",
                    top: -1,
                    left: "50%",
                    transform:  "translateX(-50%)",
                    bgcolor:    "primary.main",
                    color:      "#fff",
                    fontSize:   "9px",
                    fontWeight:  700,
                    px:         "4px",
                    borderRadius:"2px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Today
                </Box>
              </Box>
            )}

            {enriched.map((row, rowIdx) => renderBar(row, rowIdx))}
          </Box>
        </Box>
      </Box>

      {/* Empty state */}
      {enriched.length === 0 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "text.secondary" }}>
          <Typography variant="body2">No tasks to display</Typography>
        </Box>
      )}
    </Box>
  );
};

export default InlineGantt;
